'use client';

import { Avatar, Box, Button, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { MouseEvent, ReactElement, useState } from 'react';

import { ConditionalWrapper } from '@repo/cc-ds';

import styles from './app-header-avatar-menu.module.scss';

type Avatar = {
  tooltip?: string;
} & (
  | {
      image: {
        alt: string;
        src: string;
      };
      label?: never;
    }
  | {
      image?: never;
      label: string;
    }
);

export type AppHeaderAvatarBaseMenuProps =
  | {
      avatar?: never;
      button: {
        label: string;
        onClick: () => void;
        tooltip?: string;
      };
      menuItems?: never;
    }
  | {
      avatar: Avatar;
      button?: never;
      menuItems?: {
        label: string;
        onClick: () => void;
      }[];
    };

type AppHeaderAvatarMenuProps = {
  testId?: string;
} & AppHeaderAvatarBaseMenuProps;

export const AppHeaderAvatarMenu = (props: AppHeaderAvatarMenuProps) => {
  const { avatar, button, menuItems, testId } = props;

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => setAnchorElUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  return (
    <Box
      data-testid={testId}
      className={styles.AppHeaderAvatar}
    >
      {avatar ? (
        <>
          <ConditionalWrapper
            condition={avatar.tooltip !== undefined}
            renderWithWrapper={(children) => (
              <Tooltip
                arrow
                data-testid={testId && `${testId}-avatarTooltip`}
                enterDelay={500}
                title={avatar.tooltip}
              >
                {children as ReactElement}
              </Tooltip>
            )}
          >
            <IconButton
              className={styles.avatarButton}
              data-testid={testId && `${testId}-avatarButton`}
              onClick={handleOpenUserMenu}
            >
              <Avatar
                data-testid={testId && `${testId}-avatar`}
                {...avatar.image}
              >
                {avatar.label ? avatar.label : null}
              </Avatar>
            </IconButton>
          </ConditionalWrapper>
          <Menu
            anchorEl={anchorElUser}
            anchorOrigin={{
              horizontal: 'right',
              vertical: 'bottom',
            }}
            className={styles.menu}
            data-testid={testId && `${testId}-menu`}
            keepMounted
            onClose={handleCloseUserMenu}
            open={Boolean(anchorElUser)}
            transformOrigin={{
              horizontal: 'right',
              vertical: 'top',
            }}
          >
            {menuItems?.map((menuItem) => (
              <MenuItem
                key={menuItem.label}
                onClick={() => {
                  menuItem.onClick();
                  handleCloseUserMenu();
                }}
              >
                <Typography
                  data-testid={testId && `${testId}-menuItem-${menuItem.label}`}
                  textAlign="center"
                >
                  {menuItem.label}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}

      {button ? (
        <ConditionalWrapper
          condition={button.tooltip !== undefined}
          renderWithWrapper={(children) => (
            <Tooltip
              arrow
              data-testid={testId && `${testId}-buttonTooltip`}
              enterDelay={500}
              title={button.tooltip}
            >
              {children as ReactElement}
            </Tooltip>
          )}
        >
          <Button
            data-testid={testId && `${testId}-button`}
            onClick={button.onClick}
          >
            {button.label}
          </Button>
        </ConditionalWrapper>
      ) : null}
    </Box>
  );
};
